<script>
    import { afterUpdate, onMount } from "svelte";

    let messages = [];
    let newMessage = "";
    let loading = false;
    let chatContainer;
    let textareaElement;

    onMount(() => {
        // Add a welcome message when the component mounts
        messages = [
            {
                sender: "system",
                text: "Hello! Ask me anything about Nathan's resume and experience. \n Please be patient, resume-bot running on 1gb of RAM and on 1 cpu 💸",
                timestamp: new Date(),
            },
        ];

        // Set initial height of textarea
        if (textareaElement) {
            adjustTextareaHeight();
        }
    });

    // Auto-scroll to bottom when messages update
    afterUpdate(() => {
        if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
        }
    });

    // Format message text with code blocks
    function formatMessage(text) {
        // Check if text contains code blocks
        if (!text.includes("```")) {
            return text;
        }

        // Split by code block markers
        const parts = text.split(/(```(?:[\w]*\n)?|```)/g);
        let isInCodeBlock = false;
        let language = "";

        // Process each part
        const formatted = parts.map((part, index) => {
            // Check for code block start
            if (part.startsWith("```") && !isInCodeBlock) {
                isInCodeBlock = true;

                // Extract language if specified
                const match = part.match(/```([\w]*)\n?/);
                language = match && match[1] ? match[1] : "";

                return "";
            }

            // Check for code block end
            if (part === "```" && isInCodeBlock) {
                isInCodeBlock = false;
                return "";
            }

            // Format code block content
            if (isInCodeBlock) {
                return `<pre class="code-block ${language}"><code>${part.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</code></pre>`;
            }

            // Regular text - replace newlines with <br>
            return part.replace(/\n/g, "<br>");
        });

        return formatted.join("");
    }

    // Auto-resize textarea as user types
    function adjustTextareaHeight() {
        if (!textareaElement) return;

        // Reset height to get the correct scrollHeight
        textareaElement.style.height = "auto";

        // Set new height based on content (with max height)
        const newHeight = Math.min(textareaElement.scrollHeight, 150);
        textareaElement.style.height = `${newHeight}px`;
    }

    function handleInput() {
        adjustTextareaHeight();
    }

    async function sendMessage() {
        if (!newMessage.trim()) return;

        // Add user message to chat
        const userMessage = {
            sender: "user",
            text: newMessage,
            timestamp: new Date(),
        };

        messages = [...messages, userMessage];
        const query = newMessage;
        newMessage = "";

        // Reset textarea height after clearing
        if (textareaElement) {
            textareaElement.style.height = "auto";
        }

        loading = true;

        try {
            // Send request to gateway which routes to RAG server
            const response = await fetch("/rag/query/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    query: query,
                    top_k: 3,
                }),
                credentials: "include",
                // Set a long timeout to handle potentially long-running LLM responses
                signal: AbortSignal.timeout(90000), // 90 seconds
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage =
                    errorData.detail ||
                    `Server responded with ${response.status}`;
                throw new Error(errorMessage);
            }

            const data = await response.json();

            // Add bot response to chat
            const botMessage = {
                sender: "bot",
                text: data.response,
                timestamp: new Date(),
            };

            messages = [...messages, botMessage];
        } catch (error) {
            console.error("Error:", error);

            // Add specific error message to chat
            const errorMessage = {
                sender: "system",
                text:
                    error.name === "TimeoutError"
                        ? "The request timed out. The model might be taking too long to respond."
                        : `Error: ${error.message || "Something went wrong. Please try again later."}`,
                timestamp: new Date(),
            };

            messages = [...messages, errorMessage];
        } finally {
            loading = false;
        }
    }

    function handleKeyDown(event) {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
        }
    }

    function formatTime(date) {
        return date.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
        });
    }
</script>

<div class="chat-container">
    <div class="chat-messages" id="chat-messages" bind:this={chatContainer}>
        {#each messages as message}
            <div class="message {message.sender}">
                <div class="message-content">
                    <div class="message-text">
                        {@html formatMessage(message.text)}
                    </div>
                    <div class="message-time">
                        {formatTime(message.timestamp)}
                    </div>
                </div>
            </div>
        {/each}

        {#if loading}
            <div class="message bot loading">
                <div class="message-content">
                    <div class="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </div>
        {/if}
    </div>

    <div class="chat-input">
        <textarea
            bind:value={newMessage}
            bind:this={textareaElement}
            on:keydown={handleKeyDown}
            on:input={handleInput}
            placeholder="Ask about Nathan's experience..."
            rows="1"
        ></textarea>
        <button on:click={sendMessage} disabled={loading || !newMessage.trim()}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
        </button>
    </div>
</div>

<style>
    .chat-container {
        display: flex;
        flex-direction: column;
        height: calc(100vh - 100px);
        max-width: 800px;
        margin: 0 auto;
        border-radius: 8px;
        overflow: hidden;
    }

    .chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
    }

    .message {
        display: flex;
        margin: 0.2rem 0;
    }

    .message.user {
        justify-content: flex-end;
    }

    .message-content {
        max-width: 80%;
        padding: 0.8rem 1rem;
        border-radius: 1rem;
        position: relative;
    }

    .user .message-content {
        background-color: #64ffda;
        color: #0a192f;
        border-top-right-radius: 0.2rem;
    }

    :global(body.light-mode) .user .message-content {
        background-color: #2d5a27;
        color: white;
    }

    .bot .message-content {
        background-color: #112240;
        color: #ccd6f6;
        border-top-left-radius: 0.2rem;
    }

    :global(body.light-mode) .bot .message-content {
        background-color: #f0f0f0;
        color: #2d5a27;
    }

    .system .message-content {
        background-color: #233554;
        color: #8892b0;
        width: 100%;
        text-align: center;
        border-radius: 0.5rem;
    }

    :global(body.light-mode) .system .message-content {
        background-color: #e0e0e0;
        color: #555;
    }

    .message-time {
        font-size: 0.7rem;
        opacity: 0.7;
        margin-top: 0.3rem;
        text-align: right;
    }

    .chat-input {
        display: flex;
        padding: 1rem;
        background-color: #0a192f;
        border-top: 1px solid #233554;
    }

    :global(body.light-mode) .chat-input {
        background-color: #ffffff;
        border-top: 1px solid #e0e0e0;
    }

    textarea {
        flex: 1;
        border: none;
        background-color: #112240;
        color: #ccd6f6;
        padding: 0.8rem;
        border-radius: 1.5rem;
        resize: none;
        outline: none;
        max-height: 100px;
        font-family: inherit;
        font-size: 0.9rem;
    }

    :global(body.light-mode) textarea {
        background-color: #f0f0f0;
        color: #333;
    }

    button {
        background-color: #64ffda;
        color: #0a192f;
        border: none;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        margin-left: 0.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    :global(body.light-mode) button {
        background-color: #2d5a27;
        color: white;
    }

    button:hover {
        transform: scale(1.05);
    }

    button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    button svg {
        width: 18px;
        height: 18px;
    }

    .typing-indicator {
        display: flex;
        align-items: center;
        gap: 0.3rem;
    }

    .typing-indicator span {
        display: block;
        width: 8px;
        height: 8px;
        background-color: #64ffda;
        border-radius: 50%;
        animation: typing 1s infinite ease-in-out;
    }

    :global(body.light-mode) .typing-indicator span {
        background-color: #2d5a27;
    }

    .typing-indicator span:nth-child(1) {
        animation-delay: 0s;
    }

    .typing-indicator span:nth-child(2) {
        animation-delay: 0.2s;
    }

    .typing-indicator span:nth-child(3) {
        animation-delay: 0.4s;
    }

    @keyframes typing {
        0%,
        100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-5px);
        }
    }

    /* Responsive styling */
    @media (max-width: 768px) {
        .chat-container {
            height: calc(100vh - 80px);
            border-radius: 0;
        }

        .message-content {
            max-width: 85%;
            padding: 0.6rem 0.8rem;
        }

        textarea {
            padding: 0.6rem;
            font-size: 0.85rem;
        }

        button {
            width: 36px;
            height: 36px;
        }
    }

    @media (max-width: 480px) {
        .chat-container {
            height: calc(100vh - 70px);
        }

        .chat-messages {
            padding: 0.8rem;
        }

        .message-content {
            max-width: 90%;
            padding: 0.5rem 0.7rem;
            font-size: 0.9rem;
        }

        .chat-input {
            padding: 0.8rem;
        }

        textarea {
            padding: 0.5rem;
            font-size: 0.8rem;
        }

        button {
            width: 32px;
            height: 32px;
            margin-left: 0.3rem;
        }

        button svg {
            width: 16px;
            height: 16px;
        }
    }

    .code-block {
        background: #1a1a1a;
        padding: 0.75rem;
        border-radius: 0.5rem;
        overflow-x: auto;
        font-family: "Consolas", "Monaco", "Courier New", monospace;
        font-size: 0.9rem;
        line-height: 1.4;
        margin: 0.5rem 0;
        color: #e0e0e0;
    }

    :global(body.light-mode) .code-block {
        background: #f2f2f2;
        color: #333;
    }

    /* Update message text to handle HTML content */
    .message-text {
        word-break: break-word;
        white-space: pre-wrap;
        line-height: 1.4;
    }
</style>
